import { useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { FormStates, StateValue } from "./FormStateMachine";
import { CorporateDateDetails } from "./corporatedetails/CorporateDateDetails";
import { useCorpStore } from "@/reducers/corporateReducer";
import { CorporateInfo } from "./corporatedetails/CorporateInfo";
import { CorporateBusinessAddress } from "./corporatedetails/CorporateBusinessAddress";
import DirectorInfo from "./directordetails/DirectorInfo";
import DirectorPepInfo from "./directordetails/DirectorPepInfo";
import CorporateIssuerDocuments from "./corporatedetails/CorporateIssuerUpload";
import CorporateArticleOfIncorporationUpload from "./corporatedetails/CorporateDocuments";
import RegistrationComplete from "./RegistrationCompletePage";
import CorporateAdditionalDetails from "./corporatedetails/CorporateAdditionalDetails";
import DirectorPersonalShare from "./directordetails/DirectorPersonalShare";
import AuthorizedDirector from "./directordetails/AuthorizedDirector";
import { DarkModeMenu } from "../signup/DarkmodeSignUpPanel";
import CorporateAssist from "./corporatedetails/CorporateAssist";

export const ROUTES = {
  CORPORATE_INFO: "/corporate-info",
  CORPORATE_BUSINESS_ADDRESS: "/corporate-business-address",
  CORPORATE_BUSINESS_DETAILS: "/corporate-business-details",
  CORPORATE_ADDITIONAL_DETAILS: "/corporate-additional-details",
  CORPORATE_DATE_DETAILS: "/corporate",
  DIRECTOR_INFO: "/director-info",
  DIRECTOR_PEP_INFO: "/director-pep-info",
  CORPORATE_DOCUMENTS: "/corporate-documents",
  COMPLETE: "/registration-complete",
  DIRECTOR_AUTH_INFO: "/director-auth-info",
  CORPORATE_ASSIST: "/corporate-assist",
  CORPORATE_ARTICLE_INCORPORATION: "/corporate-article-incorporation",
  DIRECTOR_PERSONAL_SHARE: "/director-personal-share",
} as const;

const STATE_TO_ROUTE: Record<keyof typeof FormStates, string> = {
  [FormStates.CORPINFO]: ROUTES.CORPORATE_INFO,
  [FormStates.CORP_BUS_ADDRESS]: ROUTES.CORPORATE_BUSINESS_ADDRESS,
  [FormStates.CORP_ADDN_DETAILS]: ROUTES.CORPORATE_ADDITIONAL_DETAILS,
  [FormStates.CORP_DATE_DETAILS]: ROUTES.CORPORATE_DATE_DETAILS,
  [FormStates.DIRECTOR_INFO]: ROUTES.DIRECTOR_INFO,
  [FormStates.DIRECTOR_PEP_INFO]: ROUTES.DIRECTOR_PEP_INFO,
  [FormStates.CORPDOCS]: ROUTES.CORPORATE_DOCUMENTS,
  [FormStates.DIRECTOR_AUTH_INFO]: ROUTES.DIRECTOR_AUTH_INFO,
  [FormStates.CORPORATE_ARTICLE_INCORPORATION]:
    ROUTES.CORPORATE_ARTICLE_INCORPORATION,
  [FormStates.COMPLETE]: ROUTES.COMPLETE,
  [FormStates.DIRECTOR_PERSONAL_SHARE]: ROUTES.DIRECTOR_PERSONAL_SHARE,
  [FormStates.CORPORATE_ASSIST]: ROUTES.CORPORATE_ASSIST,
};

const ROUTE_TO_STATE: Record<string, keyof typeof FormStates> = {
  [ROUTES.CORPORATE_INFO]: FormStates.CORPINFO,
  [ROUTES.CORPORATE_BUSINESS_ADDRESS]: FormStates.CORP_BUS_ADDRESS,
  [ROUTES.CORPORATE_ADDITIONAL_DETAILS]: FormStates.CORP_ADDN_DETAILS,
  [ROUTES.CORPORATE_DATE_DETAILS]: FormStates.CORP_DATE_DETAILS,
  [ROUTES.DIRECTOR_INFO]: FormStates.DIRECTOR_INFO,
  [ROUTES.DIRECTOR_PEP_INFO]: FormStates.DIRECTOR_PEP_INFO,
  [ROUTES.DIRECTOR_AUTH_INFO]: FormStates.DIRECTOR_AUTH_INFO,
  [ROUTES.CORPORATE_DOCUMENTS]: FormStates.CORPDOCS,
  [ROUTES.DIRECTOR_PERSONAL_SHARE]: FormStates.DIRECTOR_PERSONAL_SHARE,
  [ROUTES.CORPORATE_ARTICLE_INCORPORATION]:
    FormStates.CORPORATE_ARTICLE_INCORPORATION,
  [ROUTES.CORPORATE_ASSIST]: FormStates.CORPORATE_ASSIST,
  [ROUTES.COMPLETE]: FormStates.COMPLETE,
};
const getFormPanel = (state: StateValue) => {
  switch (state) {
    case FormStates.CORPINFO:
      return <CorporateInfo />;
    case FormStates.DIRECTOR_PERSONAL_SHARE:
      return <DirectorPersonalShare />;
    case FormStates.CORP_BUS_ADDRESS:
      return <CorporateBusinessAddress />;
    case FormStates.DIRECTOR_AUTH_INFO:
      return <AuthorizedDirector />;
    case FormStates.CORP_ADDN_DETAILS:
      return <CorporateAdditionalDetails />;
    case FormStates.CORP_DATE_DETAILS:
      return <CorporateDateDetails />;
    case FormStates.CORPDOCS:
      return <CorporateIssuerDocuments />;
    case FormStates.CORPORATE_ARTICLE_INCORPORATION:
      return <CorporateArticleOfIncorporationUpload />;
    case FormStates.DIRECTOR_INFO:
      return <DirectorInfo />;
    case FormStates.DIRECTOR_PEP_INFO:
      return <DirectorPepInfo />;
    case FormStates.CORPORATE_ASSIST:
      return <CorporateAssist />;
    case FormStates.COMPLETE:
      return <RegistrationComplete />;
    default:
      return <Navigate to={STATE_TO_ROUTE[FormStates.CORPINFO]} replace />;
  }
};

export const CorporateSignupPanel = () => {
  const location = useLocation();
  const formState = useCorpStore((state) => state.formState);
  const jumpTo = useCorpStore((state) => state.jumpTo);

  // Sync route with form state
  useEffect(() => {
    const currentState = ROUTE_TO_STATE[location.pathname];
    if (currentState && currentState !== formState) {
      jumpTo(currentState);
    }
  }, [location.pathname, formState, jumpTo]);

  // Get the current panel based on form state
  const currentPanel = getFormPanel(formState);

  // Get the current step name for test ID
  const getCurrentStepName = () => {
    if (!formState) return "unknown";
    return formState.toLowerCase().replace(/_/g, "-");
  };

  return (
    <div
      data-testid="container-corporate-signup-container"
      className="relative p-5 md:pt-20 md:pb-0 w-full max-w-3xl mx-auto"
    >
      {window.self === window.top && (
        <div
          data-testid="container-corporate-signup-darkmode"
          className="absolute top-4 right-0 md:top-10 md:right-0"
        >
          <DarkModeMenu />
        </div>
      )}
      <div
        data-testid="container-corporate-signup-content"
        className="flex-grow"
      >
        <div data-testid={`corporate-signup-step-${getCurrentStepName()}`}>
          {currentPanel}
        </div>
      </div>
      <div
        data-testid="container-corporate-signup-navigation"
        className="mt-6 flex justify-between"
      >
        {formState === FormStates.COMPLETE && (
          <button
            onClick={() => (window.location.href = "/dashboard")}
            data-testid="btn-corporate-signup-next"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Code Improvement Suggestions:
 *
 * 1. The component relies on global state management through the useCorpStore hook, which could make testing and isolation difficult.
 * 2. The routing logic is complex and tightly coupled with the form state machine, consider separating these concerns.
 * 3. There's no loading state or error handling for potential async operations in child components.
 * 4. The darkmode toggle is conditionally rendered based on window.self === window.top, but lacks comments explaining this check.
 * 5. Consider using React.lazy and Suspense for code-splitting to improve initial load time for this complex multi-step form.
 * 6. The component lacks memoization which could lead to unnecessary re-renders when parent components change.
 * 7. There's no explicit accessibility considerations for screen readers navigating through a multi-step form.
 * 8. The current approach uses route-based state management which could be enhanced with a more declarative approach.
 */
